import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  ScanSearch,
  FolderArchive,
  Images,
  Users,
  MapPinned,
  Landmark,
  Film,
  Camera,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

function cn(...s) {
  return s.filter(Boolean).join(" ");
}

function useInterval(callback, delay) {
  const saved = useRef(callback);

  useEffect(() => {
    saved.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay == null) return;
    const id = setInterval(() => saved.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

export default function Home() {
  const shouldReduce = useReducedMotion();

  const slides = useMemo(
    () => [
      { src: "/ritratto.jpg", label: "Ritratto d’epoca", icon: Camera },
      { src: "/vita.jpg", label: "Vita quotidiana", icon: Users },
      { src: "/storia.jpg", label: "Veduta storica", icon: Landmark },
      { src: "/famiglia.jpg", label: "Fondo fotografico", icon: FolderArchive },
      { src: "/cinema.jpg", label: "Cinema & personalità", icon: Film },
    ],
    []
  );

  const indexCards = useMemo(
    () => [
      {
        to: "/archivi-900",
        tag: "Archivio",
        badge: "’900",
        title: "Archivi ’900",
        text: "Collezioni, serie e racconti del Novecento.",
        icon: FolderArchive,
      },
      {
        to: "/fondi",
        tag: "Fondi",
        badge: "Autori",
        title: "Fondi fotografici",
        text: "Autori, archivi personali e materiali d’epoca.",
        icon: ShieldCheck,
      },
      {
        to: "/album-foto",
        tag: "Album",
        badge: "Temi",
        title: "Album fotografici",
        text: "Reportage, cinema, personalità e vita quotidiana.",
        icon: Images,
      },
      {
        to: "/album-di-famiglia",
        tag: "Album",
        badge: "Famiglia",
        title: "Album di famiglia",
        text: "Ritratti, nuclei familiari e memoria privata.",
        icon: Users,
      },
      {
        to: "/agro",
        tag: "Territorio",
        badge: "Agro",
        title: "Agro",
        text: "Percorsi e album dedicati all’Agro.",
        icon: MapPinned,
      },
      {
        to: "/diano",
        tag: "Territorio",
        badge: "Diano",
        title: "Valle di Diano",
        text: "Percorsi e album dedicati alla Valle di Diano.",
        icon: Landmark,
      },
    ],
    []
  );

  const [i, setI] = useState(0);
  const [mx, setMx] = useState(0);
  const [my, setMy] = useState(0);

  const [activeCard, setActiveCard] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [gridX, setGridX] = useState(50);
  const [gridY, setGridY] = useState(50);

  useInterval(() => setI((v) => (v + 1) % slides.length), 5200);
  useInterval(() => {
    if (hoveredCard !== null) return;
    setActiveCard((v) => (v + 1) % indexCards.length);
  }, 2600);

  const active = slides[i];
  const ActiveIcon = active.icon;

  return (
<main className="min-h-screen pt-20 bg-[linear-gradient(180deg,#f6f2ee_0%,#fcfbfa_22%,#ffffff_48%,#f8fafc_100%)] text-slate-900">      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-slate-200/70"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          setMx((x - 0.5) * 16);
          setMy((y - 0.5) * 16);
        }}
        onMouseLeave={() => {
          setMx(0);
          setMy(0);
        }}
      >
        <div className="absolute inset-0">
          {slides.map((s, idx) => (
            <motion.div
              key={s.src}
              className="absolute inset-0"
              initial={false}
              animate={{
                opacity: idx === i ? 1 : 0,
                scale: shouldReduce ? 1 : idx === i ? 1.06 : 1.02,
              }}
              transition={{ duration: 1.0, ease: "easeOut" }}
            >
              <img src={s.src} alt={s.label} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[radial-gradient(1200px_520px_at_20%_10%,rgba(255,255,255,.18),transparent_60%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(250,246,242,0.70)_0%,rgba(255,255,255,0.78)_38%,rgba(248,250,252,0.96)_100%)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.58),transparent_32%),radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_24%)]" />
            </motion.div>
          ))}

          <motion.div
            aria-hidden
            className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full blur-3xl opacity-70"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(204,58,33,.22), transparent 60%), radial-gradient(circle at 70% 70%, rgba(229,142,124,.16), transparent 62%)",
              transform: `translate3d(${mx}px, ${my}px, 0)`,
            }}
          />
          <motion.div
            aria-hidden
            className="absolute -bottom-48 -right-48 h-[560px] w-[560px] rounded-full blur-3xl opacity-60"
            style={{
              background:
                "radial-gradient(circle at 30% 30%, rgba(251,191,36,.14), transparent 62%), radial-gradient(circle at 70% 70%, rgba(204,58,33,.12), transparent 64%)",
              transform: `translate3d(${-mx}px, ${-my}px, 0)`,
            }}
          />

          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,rgba(255,255,255,.35)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,.22)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:radial-gradient(60%_55%_at_50%_20%,#000_60%,transparent_100%)]" />
          <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay [background-image:url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22240%22 height=%22240%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22240%22 height=%22240%22 filter=%22url(%23n)%22 opacity=%220.35%22/%3E%3C/svg%3E')]" />
        </div>

        <div className="relative mx-auto w-[min(1200px,92%)] pb-10 pt-16 md:pb-12 md:pt-20">
  <div className="mx-auto flex min-h-[72svh] max-w-3xl flex-col items-center justify-center text-center md:min-h-0 md:items-start md:justify-start md:text-left">
    {/* SOLO DESKTOP/TABLET */}
    <motion.div
      initial={shouldReduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#CC3A21]/20 bg-white/70 px-4 py-2 text-[#7A2417] backdrop-blur-md shadow-[0_18px_60px_rgba(15,23,42,.10)]"
    >
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#CC3A21] shadow-[0_12px_26px_rgba(204,58,33,.25)]">
        <Sparkles className="h-4 w-4 text-white" />
      </span>
      <span className="font-extrabold tracking-tight">Museo Didattico della Fotografia</span>
      <span className="ml-1 rounded-full border border-[#CC3A21]/15 bg-[#CC3A21]/[0.06] px-2 py-0.5 text-xs font-extrabold text-[#8B3A2B]">
        Archivio
      </span>
    </motion.div>

    <motion.h1
      initial={shouldReduce ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.65, delay: 0.05, ease: "easeOut" }}
      className="text-[clamp(2.2rem,8vw,4.4rem)] font-black leading-[1.02] tracking-[-0.05em] text-slate-900 drop-shadow-[0_18px_40px_rgba(255,255,255,.35)] md:mt-5"
    >
      Archivio{" "}
      <span className="relative inline-block">
        <span className="bg-[linear-gradient(135deg,#7A2417,#CC3A21)] bg-clip-text text-transparent">
          MuDiF
        </span>
        <span className="absolute -bottom-3 left-[6%] right-[6%] h-3 rounded-full bg-[radial-gradient(50%_120%_at_50%_50%,rgba(204,58,33,.22),transparent_70%)] blur-xl opacity-90" />
      </span>
    </motion.h1>

    {/* SOLO DESKTOP/TABLET */}
    <motion.p
      initial={shouldReduce ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.65, delay: 0.12, ease: "easeOut" }}
      className="mt-4 hidden text-[clamp(1rem,2vw,1.2rem)] font-semibold leading-relaxed text-slate-700 md:block"
    >
      Memorie visive, fondi, album e racconti fotografici che custodiscono l’identità del territorio.
    </motion.p>

    <motion.div
      initial={shouldReduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.18, ease: "easeOut" }}
      className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row md:mt-7 md:flex-wrap"
    >
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent("mudif:open-sections"));
        }}
        className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-2xl bg-slate-900 px-5 py-3 font-extrabold text-white shadow-[0_22px_70px_rgba(15,23,42,.20)] transition-transform hover:-translate-y-0.5 hover:bg-black"
      >
        <span className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:radial-gradient(420px_140px_at_20%_0%,rgba(204,58,33,.26),transparent_60%),radial-gradient(420px_140px_at_90%_110%,rgba(229,142,124,.20),transparent_62%)]" />
        <span className="relative inline-flex items-center gap-3">
          <ScanSearch className="h-5 w-5" />
          Esplora l’Archivio
          <ChevronRight className="h-5 w-5 opacity-90 transition-transform group-hover:translate-x-0.5" />
        </span>
      </button>

      <a
        href="https://ildidrammo.com/home"
        target="_blank"
        rel="noreferrer"
        className="group inline-flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white/90 px-5 py-3 font-extrabold text-slate-900 shadow-[0_18px_60px_rgba(15,23,42,.08)] backdrop-blur-md transition-transform hover:-translate-y-0.5 hover:bg-white"
      >
        <ArrowUpRight className="h-5 w-5" />
        Ritorna al Sito principale
        <span className="opacity-90 transition-transform group-hover:translate-x-0.5">→</span>
      </a>
    </motion.div>

    {/* SOLO DESKTOP/TABLET */}
    <motion.div
      initial={shouldReduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.24, ease: "easeOut" }}
      className="mt-7 hidden flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:flex"
    >
      <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/85 px-3 py-2 text-sm font-extrabold text-slate-700 backdrop-blur-md">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-[#CC3A21]/[0.08]">
          <ActiveIcon className="h-4 w-4 text-[#7A2417]" />
        </span>
        <span className="opacity-85">In evidenza:</span>
        <span className="text-slate-900">{active.label}</span>
      </div>

      <div className="flex items-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setI(idx)}
            aria-label={`Vai a slide ${idx + 1}`}
            className={cn(
              "h-2.5 w-2.5 rounded-full border border-[#CC3A21]/20 transition-all",
              idx === i
                ? "w-7 bg-[linear-gradient(135deg,#CC3A21,#E58E7C)] shadow-[0_14px_30px_rgba(204,58,33,.24)]"
                : "bg-white/70 hover:bg-white"
            )}
          />
        ))}
      </div>
    </motion.div>
  </div>
</div>
      </section>

      {/* INDEX DINAMICO */}
      <section className="mt-8 relative mx-auto w-[min(1200px,92%)] pb-16">
        <div className="relative overflow-hidden rounded-[34px] border border-slate-200/80 bg-white/80 p-6 shadow-[0_24px_90px_rgba(15,23,42,.10)] backdrop-blur-xl md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-100 [background:radial-gradient(900px_340px_at_8%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(900px_340px_at_100%_100%,rgba(251,191,36,.08),transparent_60%),radial-gradient(700px_280px_at_50%_50%,rgba(229,142,124,.08),transparent_70%)]" />
          <div className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,rgba(15,23,42,.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,.08)_1px,transparent_1px)] [background-size:64px_64px]" />

          <motion.div
            initial={shouldReduce ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="relative"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#CC3A21]/15 bg-white px-3 py-2 text-[12px] font-black tracking-wide text-slate-700 shadow-[0_12px_34px_rgba(15,23,42,.06)]">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#CC3A21]">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </span>
              Navigazione rapida
            </div>

            <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <h2 className="text-[clamp(1.6rem,3vw,2.5rem)] font-black tracking-[-0.04em] text-slate-950">
                  Scegli cosa vuoi esplorare
                </h2>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600 md:text-[15px]">
                  Una griglia viva, più coerente con la navbar: evidenziazioni dinamiche, focus automatico e interazione più moderna.
                </p>
              </div>

              <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-3 py-2 text-xs font-black text-white shadow-[0_16px_40px_rgba(15,23,42,.18)]">
                <span className="h-2.5 w-2.5 rounded-full bg-[#CC3A21] shadow-[0_0_0_6px_rgba(204,58,33,.12)]" />
                {indexCards[hoveredCard ?? activeCard]?.title}
              </div>
            </div>
          </motion.div>

          <div
            className="relative mt-7"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setGridX(((e.clientX - rect.left) / rect.width) * 100);
              setGridY(((e.clientY - rect.top) / rect.height) * 100);
            }}
          >
            <div
              className="pointer-events-none absolute inset-0 rounded-[30px] opacity-100 transition duration-300"
              style={{
                background: `radial-gradient(520px 220px at ${gridX}% ${gridY}%, rgba(204,58,33,.10), transparent 60%)`,
              }}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {indexCards.map((c, idx) => {
                const Icon = c.icon;
                const isHot = (hoveredCard ?? activeCard) === idx;

                return (
                  <motion.div
                    key={c.to}
                    initial={shouldReduce ? false : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.45, delay: idx * 0.05, ease: "easeOut" }}
                  >
                    <Link
                      to={c.to}
                      onMouseEnter={() => setHoveredCard(idx)}
                      onMouseLeave={() => setHoveredCard(null)}
                      className={cn(
                        "group relative block overflow-hidden rounded-[28px] border p-5 transition-all duration-300",
                        isHot
                          ? "border-slate-300 bg-white shadow-[0_28px_90px_rgba(15,23,42,.16)] -translate-y-1"
                          : "border-slate-200 bg-white/92 shadow-[0_18px_55px_rgba(15,23,42,.10)] hover:-translate-y-1 hover:shadow-[0_28px_90px_rgba(15,23,42,.16)]"
                      )}
                    >
                      <div className="absolute inset-0 opacity-95 [background:radial-gradient(520px_260px_at_12%_0%,rgba(204,58,33,.12),transparent_60%),radial-gradient(520px_260px_at_90%_110%,rgba(251,191,36,.08),transparent_60%)]" />
                      <div className="absolute inset-0 rounded-[28px] ring-1 ring-slate-900/8" />
                      <div className="absolute inset-0 rounded-[28px] shadow-[inset_0_1px_0_rgba(255,255,255,.7)]" />

                      <div
                        className={cn(
                          "pointer-events-none absolute -left-14 -top-14 h-40 w-40 rounded-full blur-3xl transition-opacity duration-300",
                          isHot ? "bg-[#CC3A21]/18 opacity-100" : "bg-[#CC3A21]/12 opacity-0 group-hover:opacity-100"
                        )}
                      />
                      <div
                        className={cn(
                          "pointer-events-none absolute -bottom-14 -right-14 h-40 w-40 rounded-full blur-3xl transition-opacity duration-300",
                          isHot ? "bg-amber-200/20 opacity-100" : "bg-amber-100/15 opacity-0 group-hover:opacity-100"
                        )}
                      />

                      <div className="relative flex items-start justify-between gap-3">
                        <div className="inline-flex items-center gap-2">
                          <span
                            className={cn(
                              "inline-flex h-10 w-10 items-center justify-center rounded-2xl ring-1 shadow-[0_14px_40px_rgba(15,23,42,.10)] transition-all",
                              isHot
                                ? "bg-slate-900 text-white ring-slate-900/10"
                                : "bg-slate-900/5 text-slate-900 ring-slate-900/10"
                            )}
                          >
                            <Icon className="h-5 w-5" />
                          </span>

                          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-extrabold text-slate-900 ring-1 ring-slate-900/5">
                            {c.tag}
                          </span>
                        </div>

                        <span
                          className={cn(
                            "rounded-full px-3 py-1 text-xs font-extrabold shadow-[0_16px_40px_rgba(38,46,64,.18)] transition-all",
                            isHot
                              ? "bg-[linear-gradient(135deg,#CC3A21,#E58E7C)] text-white"
                              : "bg-slate-900 text-white"
                          )}
                        >
                          {c.badge}
                        </span>
                      </div>

                      <div className="relative mt-4 text-lg font-black tracking-[-0.02em] text-slate-950">
                        {c.title}
                      </div>

                      <div className="relative mt-1 text-sm font-semibold leading-relaxed text-slate-700">
                        {c.text}
                      </div>

                      <div
                        className={cn(
                          "relative mt-5 inline-flex items-center gap-2 font-extrabold transition-colors",
                          isHot ? "text-[#CC3A21]" : "text-slate-900 group-hover:text-[#CC3A21]"
                        )}
                      >
                        Apri
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 transition-transform",
                            isHot ? "translate-x-0.5" : "group-hover:translate-x-0.5"
                          )}
                        />
                      </div>

                      <div
                        className={cn(
                          "absolute inset-x-5 bottom-0 h-[3px] rounded-full transition-all duration-300",
                          isHot
                            ? "opacity-100 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]"
                            : "opacity-0 group-hover:opacity-100 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]"
                        )}
                      />
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}